import { useMemo } from 'react';
import { withContestTargeting } from '@/config/ads.config';
import type { GoogleAdsPlacement } from '@/hooks/useTenant';

/**
 * Layer the per-request contest targeting (`pid` + `s5`) onto the admin-configured
 * over-logo placement, memoised.
 *
 * Memoised deliberately, and shared by all four over-logo call sites rather than
 * repeated at each: `AdSlot` keys its define/destroy effect on the `sizes` and
 * `targeting` *identities*, so a placement object rebuilt inline on every render tears
 * the slot down and re-requests it — a duplicate impression every time the parent
 * re-renders (a react-query refetch-on-focus is enough).
 *
 * The raw placement object is the dep, not `isPlacementEnabled()` — TenantContext
 * rebuilds that helper on every render, which would defeat the memo. Callers keep their
 * own `isLogoAdEnabled` render guard; this hook only shapes the placement.
 *
 * @param placement - `adPlacements?.SQUARES_OVER_LOGO`, as TenantContext resolved it
 * @param contestId - Contest in scope, or null/undefined outside a single contest
 */
export function useOverLogoPlacement(
  placement: GoogleAdsPlacement | null | undefined,
  contestId?: string | null
): GoogleAdsPlacement | null {
  return useMemo(() => {
    if (!placement) return null;
    return {
      ...placement,
      targeting: withContestTargeting(placement.targeting, contestId),
    };
  }, [placement, contestId]);
}
